import { useMemo } from 'react'
import { CaptionMD, TitleSM } from '@/atoms/Text'
import { useTranslate } from '@/utils/translate/translate-client'
import { FileBox } from './FileBox'
import { UploadedFile } from './Upload'
import { UploadBox } from './UploadBox'

interface Props {
  files: UploadedFile[] | null
  loadFiles: () => void
  canUpload: boolean
  torchAcknowledgement: boolean
}

const MAX_UPLOADS = 3

const useGetNumberTitle = () => {
  const { t } = useTranslate('guild-create')

  return {
    0: t('firstVideo', 'First Video'),
    1: t('secondVideoOptional', 'Second Video (Optional)'),
    2: t('thirdVideoOptional', 'Third Video (Optional)'),
  }
}

export const MultiUpload = ({ files, loadFiles, canUpload, torchAcknowledgement }: Props) => {
  const { t } = useTranslate('guild-create')
  const numberTitle = useGetNumberTitle()

  const uploadArray = useMemo(() => {
    const fileLength = files?.length || 0
    const reversedFiles = [...(files || [])].toReversed()
    if (fileLength >= MAX_UPLOADS) return reversedFiles

    const numOfPendingUpload = MAX_UPLOADS - fileLength - 1
    const pendingUploadArray = Array(numOfPendingUpload).fill({ isActive: false })

    return [...reversedFiles, { isActive: true }, ...pendingUploadArray]
  }, [files]) as (UploadedFile & { isActive: boolean })[]

  return (
    <div className="flex w-full flex-col items-center justify-center gap-8">
      <CaptionMD color="core-gray-600">
        {t(
          'youCanSubmitUpToThreeTorchesForTheGuild',
          'You can submit up to three torches for the Guild to vote on. Each torch will be shown to different Guild members and the highest scored torch will be used to determine whether to continue forward.',
        )}
      </CaptionMD>
      {uploadArray?.map((file, index) => {
        const isApproved = file.status === 'APPROVED'

        if (file?.fileName)
          return (
            <div key={file.fileName} className="flex h-64 w-full flex-col gap-4">
              <div className="flex flex-row items-center gap-2">
                <TitleSM className="font-semibold">{numberTitle[index as keyof typeof numberTitle]}</TitleSM>
                {isApproved && <span className="font-bold text-info-500">{t('approved', 'Approved')}</span>}
              </div>
              <FileBox file={file} loadFiles={loadFiles} disabled={!canUpload || isApproved} />
            </div>
          )

        return (
          <div key={index} className="flex w-full flex-col gap-4">
            <TitleSM className="font-semibold">{numberTitle[index as keyof typeof numberTitle]}</TitleSM>
            <UploadBox
              uploadedFileNames={files?.map((file) => file.fileName)}
              acknowledged={torchAcknowledgement}
              maxSize={10_000_000_000}
              acceptedFileType="video"
              loadFiles={loadFiles}
              isActive={file.isActive}
            />
          </div>
        )
      })}
    </div>
  )
}
